/**
 * Excel widget — config shape and helpers.
 *
 * Each instance is a (rows × cols) grid of cells. A cell whose `raw`
 * starts with "=" is a DSL cell and carries an optional `resolvedDsl`
 * cache (Q13) — the NL→DSL translation produced once by the DSL agent
 * and persisted alongside the dashboard so reopens don't re-run the LLM.
 */

import { newId } from '../dashboard/types';
import { isCfRule, type CfRule } from './conditionalFormatting';

export const EXCEL_WIDGET_TYPE = 'excel';

export const MAX_EXCEL_ROWS = 50;
export const MAX_EXCEL_COLS = 20;
export const DEFAULT_EXCEL_ROWS = 5;
export const DEFAULT_EXCEL_COLS = 4;

export interface ExcelCellStyle {
  textColor?: string;
  bgColor?: string;
}

export const MIN_DECIMAL_DIGITS = 0;
export const MAX_DECIMAL_DIGITS = 4;

/**
 * How a cell's value is rendered. Independent of `raw` — applies to
 * both literal cells (raw is the source) and DSL cells (the
 * evaluator's display string is reformatted).
 */
export type ExcelCellNumberFormat =
  | { kind: 'percent' }
  | { kind: 'decimal'; digits: number }
  | { kind: 'text' }
  | { kind: 'date' };

export interface ExcelCell {
  raw: string;
  resolvedDsl?: string;
  textColor?: string;
  bgColor?: string;
  numberFormat?: ExcelCellNumberFormat;
  conditionalFormatting?: CfRule[];
}

export interface CellCoord {
  r: number;
  c: number;
}

export interface ExcelWidgetConfig {
  sheetId: string;
  rows: number;
  cols: number;
  cells: ExcelCell[][];
}

const isOptionalString = (value: unknown): boolean =>
  value === undefined || typeof value === 'string';

export function isExcelCellNumberFormat(
  value: unknown,
): value is ExcelCellNumberFormat {
  if (!value || typeof value !== 'object') return false;
  const f = value as { kind?: unknown; digits?: unknown };
  if (f.kind === 'percent' || f.kind === 'text' || f.kind === 'date') {
    return true;
  }
  if (f.kind === 'decimal') {
    return (
      typeof f.digits === 'number' &&
      Number.isInteger(f.digits) &&
      f.digits >= MIN_DECIMAL_DIGITS &&
      f.digits <= MAX_DECIMAL_DIGITS
    );
  }
  return false;
}

const isOptionalNumberFormat = (value: unknown): boolean =>
  value === undefined || isExcelCellNumberFormat(value);

const isOptionalCfRules = (value: unknown): boolean => {
  if (value === undefined) return true;
  if (!Array.isArray(value)) return false;
  return value.every(isCfRule);
};

export function isExcelConfig(value: unknown): value is ExcelWidgetConfig {
  if (!value || typeof value !== 'object') return false;
  const v = value as Partial<ExcelWidgetConfig>;
  if (typeof v.sheetId !== 'string') return false;
  if (typeof v.rows !== 'number' || typeof v.cols !== 'number') return false;
  if (!Array.isArray(v.cells)) return false;
  if (v.cells.length !== v.rows) return false;
  return v.cells.every(
    (row) =>
      Array.isArray(row) &&
      row.length === v.cols &&
      row.every((cell) => {
        if (!cell || typeof cell !== 'object') return false;
        const ec = cell as ExcelCell;
        if (typeof ec.raw !== 'string') return false;
        if (!isOptionalString(ec.resolvedDsl)) return false;
        if (!isOptionalString(ec.textColor)) return false;
        if (!isOptionalString(ec.bgColor)) return false;
        if (!isOptionalNumberFormat(ec.numberFormat)) return false;
        if (!isOptionalCfRules(ec.conditionalFormatting)) return false;
        return true;
      }),
  );
}

const emptyCell = (): ExcelCell => ({ raw: '' });

const buildEmptyGrid = (rows: number, cols: number): ExcelCell[][] =>
  Array.from({ length: rows }, () => Array.from({ length: cols }, emptyCell));

export function createInitialExcelConfig(
  rows: number = DEFAULT_EXCEL_ROWS,
  cols: number = DEFAULT_EXCEL_COLS,
): ExcelWidgetConfig {
  return {
    sheetId: newId('sheet'),
    rows,
    cols,
    cells: buildEmptyGrid(rows, cols),
  };
}

/** True when `cell.raw` (after trimming leading whitespace) starts with `=`. */
export function isDslCell(cell: ExcelCell): boolean {
  return cell.raw.trimStart().startsWith('=');
}

/** Parse a literal cell's `raw` as a finite number, or null. Strips commas. */
export function parseLiteralNumber(raw: string): number | null {
  const trimmed = raw.trim().replace(/,/g, '');
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

/** 0 → "A", 25 → "Z", 26 → "AA" — the A1-style label the grid header shows. */
export function columnLabel(c: number): string {
  let n = c;
  let out = '';
  while (n >= 0) {
    out = String.fromCharCode(65 + (n % 26)) + out;
    n = Math.floor(n / 26) - 1;
  }
  return out;
}

/** Inverse of `columnLabel`. "A" → 0, "AA" → 26. Case-insensitive. */
export function columnIndex(label: string): number {
  let n = 0;
  for (const ch of label.toUpperCase()) {
    n = n * 26 + (ch.charCodeAt(0) - 64);
  }
  return n - 1;
}

/** A1-style address for a cell coordinate, e.g. (0, 0) → "A1". */
export function cellAddress(r: number, c: number): string {
  return `${columnLabel(c)}${r + 1}`;
}

/**
 * Returns the natural-language prompt portion of a DSL cell — the cell's
 * `raw` with the leading `=` stripped and trimmed. Returns null for
 * literal cells or for `=` with nothing after it.
 */
export function promptOf(cell: ExcelCell): string | null {
  const trimmed = cell.raw.trim();
  if (!trimmed.startsWith('=')) return null;
  const body = trimmed.slice(1).trim();
  return body.length > 0 ? body : null;
}

/** Append one empty row to the end of the grid (Q4). No-op at the cap. */
export function appendRow(config: ExcelWidgetConfig): ExcelWidgetConfig {
  if (config.rows >= MAX_EXCEL_ROWS) return config;
  return {
    ...config,
    rows: config.rows + 1,
    cells: [
      ...config.cells,
      Array.from({ length: config.cols }, emptyCell),
    ],
  };
}

/** Drop the trailing row. No-op at rows == 1. */
export function dropRow(config: ExcelWidgetConfig): ExcelWidgetConfig {
  if (config.rows <= 1) return config;
  return {
    ...config,
    rows: config.rows - 1,
    cells: config.cells.slice(0, -1),
  };
}

/**
 * Grow the grid to at least `rows` × `cols`, padding with empty cells.
 * Never shrinks and never exceeds the caps, so a smaller target is a
 * no-op rather than a data loss. Existing cells keep their position.
 */
export function growGrid(
  config: ExcelWidgetConfig,
  rows: number,
  cols: number,
): ExcelWidgetConfig {
  const nextRows = Math.min(MAX_EXCEL_ROWS, Math.max(config.rows, rows));
  const nextCols = Math.min(MAX_EXCEL_COLS, Math.max(config.cols, cols));
  if (nextRows === config.rows && nextCols === config.cols) return config;

  const cells = Array.from({ length: nextRows }, (_, r) =>
    Array.from({ length: nextCols }, (_, c) => config.cells[r]?.[c] ?? emptyCell()),
  );
  return { ...config, rows: nextRows, cols: nextCols, cells };
}

/** Append one empty column to every row. No-op at the cap. */
export function appendCol(config: ExcelWidgetConfig): ExcelWidgetConfig {
  if (config.cols >= MAX_EXCEL_COLS) return config;
  return {
    ...config,
    cols: config.cols + 1,
    cells: config.cells.map((row) => [...row, emptyCell()]),
  };
}

/**
 * Replace the whole grid with `values`, written row-major from A1. The
 * sheet grows to fit and never shrinks; anything past the row or column
 * cap is dropped. Every cell outside `values` ends up empty — this is a
 * whole-sheet write, not a patch, so styles and formats go with it.
 *
 * A cell may arrive with `resolvedDsl` already set, which is how a caller
 * that knows the exact DSL skips the translation step. It is dropped on
 * literal cells, keeping the Q11 invariant.
 */
export function writeSheetCells(
  config: ExcelWidgetConfig,
  values: ExcelCell[][],
): ExcelWidgetConfig {
  const neededRows = values.length;
  const neededCols = values.reduce((widest, row) => Math.max(widest, row.length), 0);
  const rows = Math.min(MAX_EXCEL_ROWS, Math.max(config.rows, neededRows));
  const cols = Math.min(MAX_EXCEL_COLS, Math.max(config.cols, neededCols));

  const cells = buildEmptyGrid(rows, cols);
  for (let r = 0; r < Math.min(rows, neededRows); r += 1) {
    const row = values[r];
    for (let c = 0; c < Math.min(cols, row.length); c += 1) {
      const cell = { ...row[c] };
      if (!isDslCell(cell)) delete cell.resolvedDsl;
      cells[r][c] = cell;
    }
  }
  return { ...config, rows, cols, cells };
}

/** True when no cell in the sheet holds anything. */
export function isSheetEmpty(config: ExcelWidgetConfig): boolean {
  return config.cells.every((row) => row.every((cell) => cell.raw.trim() === ''));
}

/** Drop the trailing column from every row. No-op at cols == 1. */
export function dropCol(config: ExcelWidgetConfig): ExcelWidgetConfig {
  if (config.cols <= 1) return config;
  return {
    ...config,
    cols: config.cols - 1,
    cells: config.cells.map((row) => row.slice(0, -1)),
  };
}

/**
 * Set the `raw` value of a cell. If the new value is a DSL cell whose
 * prompt differs from the previously-translated one, the cached
 * `resolvedDsl` is dropped (so Phase 3 will re-translate). If it's a
 * literal cell, `resolvedDsl` is dropped too (Q11 invariant: literal
 * cells must not carry resolved_dsl). Style and format fields
 * (textColor, bgColor, numberFormat) are independent of value and are
 * always preserved across raw edits.
 */
export function setCellRaw(
  config: ExcelWidgetConfig,
  r: number,
  c: number,
  raw: string,
): ExcelWidgetConfig {
  if (r < 0 || r >= config.rows || c < 0 || c >= config.cols) return config;
  const previous = config.cells[r][c];
  if (previous.raw === raw) return config;
  const next: ExcelCell = { raw };
  if (previous.textColor !== undefined) next.textColor = previous.textColor;
  if (previous.bgColor !== undefined) next.bgColor = previous.bgColor;
  if (previous.numberFormat !== undefined) next.numberFormat = previous.numberFormat;
  if (previous.conditionalFormatting !== undefined) {
    next.conditionalFormatting = previous.conditionalFormatting;
  }
  if (raw.trimStart().startsWith('=')) {
    const previousPrompt = promptOf(previous);
    const nextPrompt = promptOf(next);
    if (
      previous.resolvedDsl !== undefined &&
      previousPrompt !== null &&
      previousPrompt === nextPrompt
    ) {
      next.resolvedDsl = previous.resolvedDsl;
    }
  }
  return {
    ...config,
    cells: config.cells.map((row, ri) =>
      ri === r ? row.map((cell, ci) => (ci === c ? next : cell)) : row,
    ),
  };
}

/**
 * Set the cached `resolvedDsl` for a DSL cell. No-op if the cell is no
 * longer a DSL cell (raw was edited away from `=`) or if the value is
 * unchanged.
 */
export function setCellResolvedDsl(
  config: ExcelWidgetConfig,
  r: number,
  c: number,
  dsl: string,
): ExcelWidgetConfig {
  if (r < 0 || r >= config.rows || c < 0 || c >= config.cols) return config;
  const previous = config.cells[r][c];
  if (!isDslCell(previous)) return config;
  if (previous.resolvedDsl === dsl) return config;
  const next: ExcelCell = { ...previous, resolvedDsl: dsl };
  return {
    ...config,
    cells: config.cells.map((row, ri) =>
      ri === r ? row.map((cell, ci) => (ci === c ? next : cell)) : row,
    ),
  };
}

const inBounds = (
  config: ExcelWidgetConfig,
  r: number,
  c: number,
): boolean => r >= 0 && r < config.rows && c >= 0 && c < config.cols;

const dedupeCoords = (coords: CellCoord[]): CellCoord[] => {
  const seen = new Set<string>();
  const out: CellCoord[] = [];
  for (const { r, c } of coords) {
    const key = `${r}:${c}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ r, c });
  }
  return out;
};

/**
 * Clear the `raw` value (and any cached resolvedDsl) for each coordinate
 * in `coords`. Style and format fields (textColor, bgColor,
 * numberFormat) are preserved. Returns the same reference if every
 * targeted cell is already empty.
 */
export function clearCells(
  config: ExcelWidgetConfig,
  coords: CellCoord[],
): ExcelWidgetConfig {
  const targets = dedupeCoords(coords).filter(({ r, c }) => inBounds(config, r, c));
  if (targets.length === 0) return config;
  const targetMap = new Map<string, true>();
  let changed = false;
  for (const { r, c } of targets) {
    const cell = config.cells[r][c];
    if (cell.raw !== '' || cell.resolvedDsl !== undefined) {
      changed = true;
      targetMap.set(`${r}:${c}`, true);
    }
  }
  if (!changed) return config;
  return {
    ...config,
    cells: config.cells.map((row, ri) =>
      row.map((cell, ci) => {
        if (!targetMap.has(`${ri}:${ci}`)) return cell;
        const next: ExcelCell = { raw: '' };
        if (cell.textColor !== undefined) next.textColor = cell.textColor;
        if (cell.bgColor !== undefined) next.bgColor = cell.bgColor;
        if (cell.numberFormat !== undefined) next.numberFormat = cell.numberFormat;
        if (cell.conditionalFormatting !== undefined) {
          next.conditionalFormatting = cell.conditionalFormatting;
        }
        return next;
      }),
    ),
  };
}

/**
 * Apply a partial style patch to every coordinate in `coords`. Keys
 * present in `style` overwrite the cell's existing value; keys set to
 * `null` clear that style on the targeted cells. Returns the same
 * reference when no cell would change.
 */
export function setCellsStyle(
  config: ExcelWidgetConfig,
  coords: CellCoord[],
  style: { textColor?: string | null; bgColor?: string | null },
): ExcelWidgetConfig {
  const targets = dedupeCoords(coords).filter(({ r, c }) => inBounds(config, r, c));
  if (targets.length === 0) return config;
  const hasTextColor = Object.prototype.hasOwnProperty.call(style, 'textColor');
  const hasBgColor = Object.prototype.hasOwnProperty.call(style, 'bgColor');
  if (!hasTextColor && !hasBgColor) return config;
  const targetMap = new Map<string, true>();
  for (const { r, c } of targets) targetMap.set(`${r}:${c}`, true);

  let changed = false;
  const nextCells = config.cells.map((row, ri) =>
    row.map((cell, ci) => {
      if (!targetMap.has(`${ri}:${ci}`)) return cell;
      const next: ExcelCell = { ...cell };
      if (hasTextColor) {
        if (style.textColor === null || style.textColor === undefined) {
          delete next.textColor;
        } else {
          next.textColor = style.textColor;
        }
      }
      if (hasBgColor) {
        if (style.bgColor === null || style.bgColor === undefined) {
          delete next.bgColor;
        } else {
          next.bgColor = style.bgColor;
        }
      }
      if (next.textColor === cell.textColor && next.bgColor === cell.bgColor) {
        return cell;
      }
      changed = true;
      return next;
    }),
  );
  if (!changed) return config;
  return { ...config, cells: nextCells };
}

const numberFormatsEqual = (
  a: ExcelCellNumberFormat | undefined,
  b: ExcelCellNumberFormat | undefined,
): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.kind !== b.kind) return false;
  if (a.kind === 'decimal' && b.kind === 'decimal') {
    return a.digits === b.digits;
  }
  return true;
};

/**
 * Set the `numberFormat` on every coordinate in `coords`. Pass `null`
 * to clear the format on the targeted cells. Returns the same reference
 * when no cell would change.
 */
export function setCellsFormat(
  config: ExcelWidgetConfig,
  coords: CellCoord[],
  format: ExcelCellNumberFormat | null,
): ExcelWidgetConfig {
  const targets = dedupeCoords(coords).filter(({ r, c }) => inBounds(config, r, c));
  if (targets.length === 0) return config;
  if (format !== null && !isExcelCellNumberFormat(format)) return config;
  const targetMap = new Map<string, true>();
  for (const { r, c } of targets) targetMap.set(`${r}:${c}`, true);

  let changed = false;
  const nextCells = config.cells.map((row, ri) =>
    row.map((cell, ci) => {
      if (!targetMap.has(`${ri}:${ci}`)) return cell;
      const desired = format === null ? undefined : format;
      if (numberFormatsEqual(cell.numberFormat, desired)) return cell;
      changed = true;
      const next: ExcelCell = { ...cell };
      if (desired === undefined) {
        delete next.numberFormat;
      } else {
        next.numberFormat = desired;
      }
      return next;
    }),
  );
  if (!changed) return config;
  return { ...config, cells: nextCells };
}

const cfRulesEqual = (
  a: CfRule[] | undefined,
  b: CfRule[] | undefined,
): boolean => {
  if (a === b) return true;
  if (!a || !b) return a === undefined && b === undefined;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const ra = a[i];
    const rb = b[i];
    if (ra.id !== rb.id) return false;
    if (ra.textColor !== rb.textColor) return false;
    if (ra.bgColor !== rb.bgColor) return false;
    if (ra.groups.length !== rb.groups.length) return false;
    for (let g = 0; g < ra.groups.length; g += 1) {
      const ga = ra.groups[g];
      const gb = rb.groups[g];
      if (ga.length !== gb.length) return false;
      for (let k = 0; k < ga.length; k += 1) {
        if (ga[k].op !== gb[k].op) return false;
        if (ga[k].target !== gb[k].target) return false;
      }
    }
  }
  return true;
};

/**
 * Set (or clear, with `null`) `conditionalFormatting` on every cell in
 * `coords`. The same rule list is applied to every targeted cell, by
 * value — callers should pass a fresh array; the function does not deep
 * clone. Returns the same reference when no cell would change.
 */
export function setCellsConditionalFormatting(
  config: ExcelWidgetConfig,
  coords: CellCoord[],
  rules: CfRule[] | null,
): ExcelWidgetConfig {
  const targets = dedupeCoords(coords).filter(({ r, c }) => inBounds(config, r, c));
  if (targets.length === 0) return config;
  const targetMap = new Map<string, true>();
  for (const { r, c } of targets) targetMap.set(`${r}:${c}`, true);

  const desired: CfRule[] | undefined =
    rules === null || rules.length === 0 ? undefined : rules;

  let changed = false;
  const nextCells = config.cells.map((row, ri) =>
    row.map((cell, ci) => {
      if (!targetMap.has(`${ri}:${ci}`)) return cell;
      if (cfRulesEqual(cell.conditionalFormatting, desired)) return cell;
      changed = true;
      const next: ExcelCell = { ...cell };
      if (desired === undefined) {
        delete next.conditionalFormatting;
      } else {
        next.conditionalFormatting = desired;
      }
      return next;
    }),
  );
  if (!changed) return config;
  return { ...config, cells: nextCells };
}

/** Inverse of `cellAddress`. "B3" → { r: 2, c: 1 }. Null when malformed. */
export function parseCellAddress(address: string): CellCoord | null {
  const match = /^([A-Za-z]{1,2})([1-9][0-9]*)$/.exec(address.trim());
  if (!match) return null;
  return { r: Number(match[2]) - 1, c: columnIndex(match[1]) };
}

/** Every non-empty cell keyed by A1-style address — the sheet as the agent sees it. */
export function sheetCellMap(config: ExcelWidgetConfig): Record<string, string> {
  const out: Record<string, string> = {};
  for (let r = 0; r < config.rows; r += 1) {
    for (let c = 0; c < config.cols; c += 1) {
      const { raw } = config.cells[r][c];
      if (raw.trim() === '') continue;
      out[cellAddress(r, c)] = raw;
    }
  }
  return out;
}

/**
 * Write an A1-keyed patch of raw values in one pass. Each cell follows the
 * same rules as `setCellRaw`, so styles, formats and a still-valid
 * `resolvedDsl` survive. Returns the same reference when nothing changes.
 * Throws on an address that is malformed or outside the grid — a patch that
 * does not fit the sheet is an error, never a partial write.
 */
export function applyCellPatch(
  config: ExcelWidgetConfig,
  patch: Record<string, string>,
): ExcelWidgetConfig {
  const targets: Array<{ coord: CellCoord; raw: string }> = [];
  for (const [address, raw] of Object.entries(patch)) {
    const coord = parseCellAddress(address);
    if (!coord) {
      throw new Error(`"${address}" is not a cell address.`);
    }
    if (coord.r >= config.rows || coord.c >= config.cols) {
      throw new Error(
        `Cell ${address.toUpperCase()} is outside the ${config.rows} × ${config.cols} grid.`,
      );
    }
    targets.push({ coord, raw });
  }

  let next = config;
  for (const { coord, raw } of targets) {
    next = setCellRaw(next, coord.r, coord.c, raw);
  }
  return next;
}
